import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id, Paginated } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import {
  IDepartment,
  TeamMember
} from "@/jenesys-types/jenesys-entities/config";

export interface TeamSettingsState {
  teamMembers: Paginated<TeamMember>;
  isTeamMembersLoading: boolean;
  teamMembersError?: AppError;
  departments: IDepartment[];
  isDepartmentsLoading: boolean;
  departmentsError?: AppError;
  isDepartmentAdding: boolean;
  departmentAddingError?: AppError;
  isMemberAdding: boolean;
  memberAddingError?: AppError;
  isMemberUpdating: boolean;
  memberUpdatingError?: AppError;
  isMemberDeleting: boolean;
  memberDeletingError?: AppError;
  isDisconnectingMember: boolean;
  disconnectingMemberError?: AppError;
  isInvitingMember: boolean;
  invitingMemberError?: AppError;
  isResendingEmail: boolean;
  resendingEmailError?: AppError;
}

interface LoadTeamMembersPayload {
  clientId: Id;
  params: any;
}
export type LoadTeamMembersAction = PayloadAction<LoadTeamMembersPayload>;

interface TeamSettingsFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type TeamSettingsFailureAction =
  PayloadAction<TeamSettingsFailurePayload>;

interface TeamMembersSuccessPayload {
  teamMembers: Paginated<TeamMember>;
}
export type TeamMembersSuccessAction = PayloadAction<TeamMembersSuccessPayload>;

interface LoadDepartmentsPayload {
  clientId: Id;
}
export type LoadDepartmentsAction = PayloadAction<LoadDepartmentsPayload>;

interface DepartmentsFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type DepartmentsFailureAction = PayloadAction<DepartmentsFailurePayload>;

interface DepartmentsSuccessPayload {
  departments: IDepartment[];
}
export type DepartmentsSuccessAction = PayloadAction<DepartmentsSuccessPayload>;

interface InviteMemberPayload {
  clientId: Id;
  data: any;
  onSuccess: () => void;
}

export type InviteMemberAction = PayloadAction<InviteMemberPayload>;

interface MemberFailurePayload {
  error?: AppError;
  clientId?: string;
  id?: string;
}
export type InviteMemberFailureAction = PayloadAction<MemberFailurePayload>;

export type InviteMemberSuccessAction = PayloadAction<any>;

interface MemberPayload {
  id: Id;
  clientId: Id;
  data?: any;
  onSuccess?: () => void;
}
export type UpdateMemberAction = PayloadAction<MemberPayload>;

export type UpdateMemberFailureAction = PayloadAction<MemberFailurePayload>;

interface UpdateMemberSuccessPayload {
  data: TeamMember;
}
export type UpdateMemberSuccessAction =
  PayloadAction<UpdateMemberSuccessPayload>;

export type DeleteMemberAction = PayloadAction<MemberPayload>;

export type DeleteMemberFailureAction = PayloadAction<MemberFailurePayload>;

interface DeleteMemberSuccessPayload {
  id: Id;
}

export type DeleteMemberSuccessAction =
  PayloadAction<DeleteMemberSuccessPayload>;

interface AddDepartmentPayload {
  clientId: Id;
  data: Partial<IDepartment>;
  onSuccess: (response: IDepartment) => void;
}

export type AddDepartmentAction = PayloadAction<AddDepartmentPayload>;

interface AddDepartmentFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type AddDepartmentFailureAction =
  PayloadAction<AddDepartmentFailurePayload>;

interface AddDepartmentSuccessPayload {
  department: IDepartment;
}

export type AddDepartmentSuccessAction =
  PayloadAction<AddDepartmentSuccessPayload>;

interface DisconnectMemberPayload {
  clientId: Id;
  memberId: Id;
  platform: string;
  onSuccess?: () => void;
}
export type DisconnectMemberAction = PayloadAction<DisconnectMemberPayload>;
export type InviteMemberPlatformAction = PayloadAction<DisconnectMemberPayload>;

interface DisconnectMemberFailurePayload {
  error?: AppError;
  clientId?: string;
}
export type DisconnectMemberFailureAction =
  PayloadAction<DisconnectMemberFailurePayload>;
export type InviteMemberPlatformFailureAction =
  PayloadAction<DisconnectMemberFailurePayload>;

export type DisconnectMemberSuccessAction = PayloadAction<any>;
export type InviteMemberPlatformSuccessAction = PayloadAction<any>;

export type ResendEmailAction = PayloadAction<{
  clientId: Id;
  memberId: Id;
}>;

export type ResendEmailSuccessAction = PayloadAction<{}>;

export type ResendEmailFailureAction = PayloadAction<{
  error?: AppError;
}>;

const name = "state/features/ClientSettings/Team";

export const initialState: TeamSettingsState = {
  isTeamMembersLoading: false,
  teamMembersError: undefined,
  teamMembers: { items: [] },
  departments: [],
  isDepartmentsLoading: false,
  departmentsError: undefined,
  isMemberAdding: false,
  memberAddingError: undefined,
  isMemberUpdating: false,
  memberUpdatingError: undefined,
  isMemberDeleting: false,
  memberDeletingError: undefined,
  isDepartmentAdding: false,
  departmentAddingError: undefined,
  isDisconnectingMember: false,
  disconnectingMemberError: undefined,
  isInvitingMember: false,
  invitingMemberError: undefined,
  isResendingEmail: false,
  resendingEmailError: undefined
};

const TeamSettingsSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadTeamMembers: {
      reducer: (state: TeamSettingsState, _action: LoadTeamMembersAction) => {
        if (_action.payload.params.searchQuery === "")
          state.teamMembers = { items: [] };
        state.isTeamMembersLoading = true;
      },
      prepare: (clientId: Id, params: any) => ({
        payload: { clientId, params }
      })
    },
    teamMembersFailure: {
      reducer: (
        state: TeamSettingsState,
        action: TeamSettingsFailureAction
      ) => {
        state.teamMembersError = action.payload.error;
        state.isTeamMembersLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    teamMembersSuccess: {
      reducer: (state: TeamSettingsState, action: TeamMembersSuccessAction) => {
        state.isTeamMembersLoading = false;
        state.teamMembers = action.payload.teamMembers;
      },
      prepare: (teamMembers: Paginated<TeamMember>) => ({
        payload: { teamMembers }
      })
    },
    loadDepartments: {
      reducer: (state: TeamSettingsState, _action: LoadDepartmentsAction) => {
        state.departments = [];
        state.isDepartmentsLoading = true;
      },
      prepare: (clientId: Id) => ({
        payload: { clientId }
      })
    },
    departmentsFailure: {
      reducer: (state: TeamSettingsState, action: DepartmentsFailureAction) => {
        state.departmentsError = action.payload.error;
        state.isDepartmentsLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    departmentsSuccess: {
      reducer: (state: TeamSettingsState, action: DepartmentsSuccessAction) => {
        state.isDepartmentsLoading = false;
        state.departments = action.payload.departments;
      },
      prepare: (departments: IDepartment[]) => ({
        payload: { departments }
      })
    },
    inviteMember: {
      reducer: (state: TeamSettingsState, _action: InviteMemberAction) => {
        state.isMemberAdding = true;
      },
      prepare: (clientId: Id, data: any, onSuccess: () => void) => ({
        payload: { clientId, data, onSuccess }
      })
    },
    inviteMemberFailure: {
      reducer: (
        state: TeamSettingsState,
        action: InviteMemberFailureAction
      ) => {
        state.memberAddingError = action.payload.error;
        state.isMemberAdding = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    inviteMemberSuccess: {
      reducer: (state: TeamSettingsState) => {
        state.isMemberAdding = false;
      },
      prepare: (response: any) => ({
        payload: { response }
      })
    },
    updateMember: {
      reducer: (state: TeamSettingsState, _action: UpdateMemberAction) => {
        state.isMemberUpdating = true;
      },
      prepare: (id: Id, clientId: Id, data: any, onSuccess: () => void) => ({
        payload: { id, clientId, data, onSuccess }
      })
    },
    updateMemberFailure: {
      reducer: (
        state: TeamSettingsState,
        action: UpdateMemberFailureAction
      ) => {
        state.memberUpdatingError = action.payload.error;
        state.isMemberUpdating = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    updateMemberSuccess: {
      reducer: (state: TeamSettingsState) => {
        state.isMemberUpdating = false;
      },
      prepare: (response: any) => ({
        payload: { response }
      })
    },
    deleteMember: {
      reducer: (state: TeamSettingsState, _action: UpdateMemberAction) => {
        state.isMemberDeleting = true;
      },
      prepare: (id: Id, clientId: Id, onSuccess: () => void) => ({
        payload: { id, clientId, onSuccess }
      })
    },
    deleteMemberFailure: {
      reducer: (
        state: TeamSettingsState,
        action: DeleteMemberFailureAction
      ) => {
        state.memberDeletingError = action.payload.error;
        state.isMemberDeleting = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    deleteMemberSuccess: {
      reducer: (
        state: TeamSettingsState,
        action: DeleteMemberSuccessAction
      ) => {
        state.isMemberDeleting = false;
        state.teamMembers.items = state.teamMembers.items.filter(
          (w: TeamMember) => w.id !== action.payload.id
        );
        state.teamMembers.total!! -= 1;
      },
      prepare: (id: Id) => ({
        payload: { id }
      })
    },
    addDepartment: {
      reducer: (state: TeamSettingsState, _action: AddDepartmentAction) => {
        state.isDepartmentAdding = true;
      },
      prepare: (
        clientId: Id,
        data: Partial<IDepartment>,
        onSuccess: (response: any) => void
      ) => ({
        payload: { clientId, data, onSuccess }
      })
    },
    addDepartmentFailure: {
      reducer: (
        state: TeamSettingsState,
        action: AddDepartmentFailureAction
      ) => {
        state.departmentAddingError = action.payload.error;
        state.isDepartmentAdding = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    addDepartmentSuccess: {
      reducer: (
        state: TeamSettingsState,
        action: AddDepartmentSuccessAction
      ) => {
        state.isDepartmentAdding = false;
        state.departments = [
          ...state.departments,
          ...[action.payload.department]
        ];
      },
      prepare: (department: IDepartment) => ({
        payload: { department }
      })
    },
    loadDisconnectMember: {
      reducer: (state: TeamSettingsState, _action: DisconnectMemberAction) => {
        state.isDisconnectingMember = true;
      },
      prepare: (
        clientId: Id,
        memberId: string,
        platform: string,
        onSuccess: () => void
      ) => ({
        payload: { clientId, memberId, platform, onSuccess }
      })
    },
    loadDisconnectMemberFailure: {
      reducer: (
        state: TeamSettingsState,
        action: DisconnectMemberFailureAction
      ) => {
        state.isDisconnectingMember = false;
        state.disconnectingMemberError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadDisconnectMemberSuccess: {
      reducer: (state: TeamSettingsState) => {
        state.isDisconnectingMember = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    loadInviteMember: {
      reducer: (state: TeamSettingsState, _action: DisconnectMemberAction) => {
        state.isInvitingMember = true;
      },
      prepare: (
        clientId: Id,
        memberId: string,
        platform: string,
        onSuccess: () => void
      ) => ({
        payload: { clientId, memberId, platform, onSuccess }
      })
    },
    loadInviteMemberFailure: {
      reducer: (
        state: TeamSettingsState,
        action: DisconnectMemberFailureAction
      ) => {
        state.isInvitingMember = false;
        state.invitingMemberError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    loadInviteMemberSuccess: {
      reducer: (state: TeamSettingsState) => {
        state.isInvitingMember = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    resendEmail: {
      reducer: (state: TeamSettingsState, _action: ResendEmailAction) => {
        state.isResendingEmail = true;
        state.resendingEmailError = undefined;
      },
      prepare: (clientId: Id, memberId: Id) => ({
        payload: { clientId, memberId }
      })
    },
    resendEmailSuccess: {
      reducer: (
        state: TeamSettingsState,
        _action: ResendEmailSuccessAction
      ) => {
        state.isResendingEmail = false;
      },
      prepare: () => ({
        payload: {}
      })
    },
    resendEmailFailure: {
      reducer: (state: TeamSettingsState, action: ResendEmailFailureAction) => {
        state.isResendingEmail = false;
        state.resendingEmailError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    }
  }
});

export const {
  teamMembersSuccess,
  teamMembersFailure,
  loadTeamMembers,
  departmentsSuccess,
  departmentsFailure,
  loadDepartments,
  inviteMemberSuccess,
  inviteMemberFailure,
  inviteMember,
  updateMemberSuccess,
  updateMemberFailure,
  updateMember,
  deleteMemberSuccess,
  deleteMemberFailure,
  deleteMember,
  addDepartmentSuccess,
  addDepartmentFailure,
  addDepartment,
  loadDisconnectMember,
  loadDisconnectMemberFailure,
  loadDisconnectMemberSuccess,
  loadInviteMember,
  loadInviteMemberFailure,
  loadInviteMemberSuccess,
  resendEmail,
  resendEmailSuccess,
  resendEmailFailure
} = TeamSettingsSlice.actions;

// Selectors
const selectTeamSettingsState = (state: {
  features: { TeamSettings: TeamSettingsState };
}): TeamSettingsState => state.features.TeamSettings;

export const selectTeamSettings = createSelector(
  selectTeamSettingsState,
  (state) => state
);
export const selectTeamMembers = createSelector(
  selectTeamSettingsState,
  (state) => state.teamMembers
);
export const selectisTeamMembersLoading = createSelector(
  selectTeamSettingsState,
  (state) => state.isTeamMembersLoading
);
export const selectDepartments = createSelector(
  selectTeamSettingsState,
  (state) => state.departments
);
export const selectisDepartmentsLoading = createSelector(
  selectTeamSettingsState,
  (state) => state.isDepartmentsLoading
);
export const selectisMemberAdding = createSelector(
  selectTeamSettingsState,
  (state) => state.isMemberAdding
);
export const selectisMemberUpdating = createSelector(
  selectTeamSettingsState,
  (state) => state.isMemberUpdating
);
export const selectisMemberDeleting = createSelector(
  selectTeamSettingsState,
  (state) => state.isMemberDeleting
);
export const selectisDepartmentAdding = createSelector(
  selectTeamSettingsState,
  (state) => state.isDepartmentAdding
);

export default TeamSettingsSlice.reducer;
